import { parsePhoneNumberFromString } from 'libphonenumber-js'
import { companiesId } from 'Utils/constants'

// Teléfono de contacto: por defecto Panamá.
export const DEFAULT_CONTACT_PHONE = {
  Prefix: '+507',
  countryCode: 'PA',
  number: '',
}

// Convierte el teléfono guardado (ej. "+50760001234" o "+507 6000-1234") al valor de
// PhoneNumberField.
// Un número guardado sin prefijo se asume de Panamá.
export const parseContactPhone = text => {
  const value = String(text ?? '').trim()
  if (!value) return { ...DEFAULT_CONTACT_PHONE }

  if (value.startsWith('+')) {
    const parsed = parsePhoneNumberFromString(value)
    if (parsed) {
      return {
        Prefix: `+${parsed.countryCallingCode}`,
        countryCode: parsed.country || null,
        number: parsed.nationalNumber,
      }
    }
  }

  return { ...DEFAULT_CONTACT_PHONE, number: value.replace(/\D/g, '') }
}

// Convierte el valor de PhoneNumberField al texto que se guarda, en formato E.164 sin
// espacios ni guiones (ej. "+50760001234").
export const formatContactPhone = phone => {
  const number = String(phone?.number ?? '').replace(/\D/g, '')
  if (!number) return ''
  const prefix = String(phone?.Prefix || DEFAULT_CONTACT_PHONE.Prefix).replace(
    /\D/g,
    ''
  )
  return `+${prefix}${number}`
}

export const CATALOG_IMAGE_MAX_SIZE_BYTES = 2 * 1024 * 1024
export const CATALOG_IMAGE_ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png']
export const CATALOG_IMAGE_ALLOWED_TYPES = ['image/jpeg', 'image/png']
export const CATALOG_IMAGE_ACCEPT = '.jpg,.jpeg,.png,image/jpeg,image/png'

export const CATALOG_UPLOAD_TENANT_ID = 2
export const CATALOG_UPLOAD_ENTITY_TYPE = 'Asset'
export const CATALOG_UPLOAD_INTERNAL_CODE = 'PRODUCT_CATALOG'

export const CATALOG_CSV_HEADERS = [
  'ID del catálogo',
  'SKU',
  'Precio',
  'URL de imagen',
]
// _id del batchtemplate catalog_products_bulk_upload en Mongo (mdl26).
export const CATALOG_BATCH_TEMPLATE_ID = '6acfcf0f53c389ec10c24353'
export const CATALOG_CSV_FILE_NAME = 'plantilla_catalogo.csv'

export const CATALOG_STEPS = ['GENERAL', 'SECTIONS', 'PRODUCTS']

// Código de la lista de precios regular web por compañía.
const REGULAR_PRICE_LIST_CODES = [
  { companyId: companiesId.cochez, code: '01-00' },
  { companyId: companiesId.puntoCochez, code: '01-00' },
  { companyId: companiesId.novey, code: '03-00' },
]

const normalizeSearchText = text =>
  String(text ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

// Criterio de búsqueda del selector de listas de precios: por código o descripción, sin
// distinguir mayúsculas ni acentos. Se envía al FilterSelector como searchFunction.
export const matchesPriceListSearch = (list, searchText) => {
  const search = normalizeSearchText(searchText)
  return [list?.Code, list?.Description].some(value =>
    normalizeSearchText(value).includes(search)
  )
}

export const getRegularPriceListCode = companyId => {
  const normalizedCompanyId = companyId?.toUpperCase()
  if (!normalizedCompanyId) return null

  return (
    REGULAR_PRICE_LIST_CODES.find(
      item => item.companyId?.toUpperCase() === normalizedCompanyId
    )?.code || null
  )
}

// Listas regulares web de la compañía, sin repetir la misma lista.
export const filterRegularPriceLists = (priceLists, code) => {
  if (!code) return []

  const seenIds = new Set()
  return priceLists.filter(list => {
    const id = list?.PriceListId?.toLowerCase()
    if (!id || seenIds.has(id) || list.Code?.trim() !== code) return false
    seenIds.add(id)
    return true
  })
}

export const normalizeId = id => (id ? String(id).toLowerCase() : id)

const toTrimmedString = value =>
  value === null || value === undefined ? '' : String(value).trim()

// Identificador de sección generado en el cliente (UUID v4); no se usa con fines de seguridad.
export const generateSectionId = () => {
  const cryptoApi = typeof crypto !== 'undefined' ? crypto : null
  if (cryptoApi?.randomUUID) {
    return cryptoApi.randomUUID()
  }

  const bytes = new Uint8Array(16)
  if (cryptoApi?.getRandomValues) {
    cryptoApi.getRandomValues(bytes)
  } else {
    bytes.forEach((_, index) => {
      bytes[index] = Math.floor(Math.random() * 256)
    })
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0'))

  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10, 16).join(''),
  ].join('-')
}

export const getTodayDateString = (date = new Date()) => {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

// Devuelve la clave de traducción del error o null si la imagen es válida.
export const validateCatalogImageFile = file => {
  if (!file) return 'INVALID_IMAGE_TYPE'

  const extension = file.name?.split('.').pop()?.toLowerCase()
  const hasValidExtension = CATALOG_IMAGE_ALLOWED_EXTENSIONS.includes(extension)
  const hasValidType =
    !file.type || CATALOG_IMAGE_ALLOWED_TYPES.includes(file.type)

  if (!hasValidExtension || !hasValidType) return 'INVALID_IMAGE_TYPE'
  if (file.size > CATALOG_IMAGE_MAX_SIZE_BYTES) return 'INVALID_IMAGE_SIZE'

  return null
}

export const buildCatalogImageFormData = ({ file, entityId }) => {
  const formData = new FormData()

  formData.append('FormFile', file)
  formData.append('TenantId', CATALOG_UPLOAD_TENANT_ID)
  formData.append('EntityType', CATALOG_UPLOAD_ENTITY_TYPE)
  formData.append('InternalCode', CATALOG_UPLOAD_INTERNAL_CODE)
  formData.append('EntityId', entityId)
  formData.append('Date', new Date().toISOString())

  return formData
}

export const extractUploadedFileUrl = uploadResponse =>
  uploadResponse?.Data?.[0]?.TableDoc?.FileUrl || null

// Imagen marcada como principal en el PIM o, si no hay, la primera por orden.
export const getDefaultPimImageUrl = (images = []) => {
  const sortedImages = [...(images || [])]
    .filter(image => image?.URL)
    .sort((a, b) => (a.Order ?? 0) - (b.Order ?? 0))

  const defaultImage =
    sortedImages.find(image => image.DefaultProductPicture) || sortedImages[0]

  return defaultImage?.URL || null
}

export const getCatalogProductImageUrl = product =>
  product?.customImageUrl || product?.defaultImageUrl || null

const sortByDisplayOrder = (a, b) =>
  (a.DisplayOrder ?? 0) - (b.DisplayOrder ?? 0)

export const mapCatalogFormValues = ({ quickList, catalog }) => {
  const catalogProducts = new Map(
    (catalog?.Products || []).map(product => [
      normalizeId(product.ProductId),
      product,
    ])
  )

  const products = (quickList?.ProductList || [])
    .map((product, index) => {
      const productId = normalizeId(product.ProductId)
      const saved = catalogProducts.get(productId)

      return {
        productId,
        sku: product.Sku,
        productName: product.ProductName,
        brandName: product.BrandName,
        images: (product.Images || []).filter(image => image?.URL),
        defaultImageUrl: getDefaultPimImageUrl(product.Images),
        catalogSectionId: normalizeId(saved?.CatalogSectionId) || null,
        price: saved?.Price ?? null,
        specialPrice:
          saved?.SpecialPrice !== null && saved?.SpecialPrice !== undefined
            ? String(saved.SpecialPrice)
            : '',
        customImageUrl: saved?.CustomImageUrl || '',
        sortKey: saved ? saved.DisplayOrder ?? 0 : Number.MAX_SAFE_INTEGER,
        listIndex: index,
      }
    })
    .sort((a, b) => a.sortKey - b.sortKey || a.listIndex - b.listIndex)
    .map(({ sortKey, listIndex, ...product }) => product)

  return {
    name: catalog?.Name || quickList?.Name || '',
    coverImageUrl: catalog?.CoverImageUrl || '',
    priceListId: normalizeId(catalog?.PriceListId) || '',
    endDate: catalog?.EndDate ? String(catalog.EndDate).slice(0, 10) : '',
    contactPhone: parseContactPhone(catalog?.ContactPhone),
    contactEmail: catalog?.ContactEmail || '',
    sections: [...(catalog?.Sections || [])]
      .sort(sortByDisplayOrder)
      .map(section => ({
        catalogSectionId: normalizeId(section.CatalogSectionId),
        name: section.Name || '',
        bannerImageUrl: section.BannerImageUrl || '',
        iconUrl: section.IconUrl || '',
        secondaryImages: [...(section.SecondaryImages || [])]
          .sort(sortByDisplayOrder)
          .map(image => image.FileUrl)
          .filter(Boolean),
      })),
    products,
  }
}

export const createCatalogSection = name => ({
  catalogSectionId: generateSectionId(),
  name,
  bannerImageUrl: '',
  iconUrl: '',
  secondaryImages: [],
})

export const moveItem = (list, index, direction) => {
  const targetIndex = index + direction
  if (index < 0 || targetIndex < 0 || targetIndex >= list.length) return list

  const nextList = [...list]
  ;[nextList[index], nextList[targetIndex]] = [
    nextList[targetIndex],
    nextList[index],
  ]
  return nextList
}

export const getSectionProducts = (products, sectionId) =>
  products.filter(product => product.catalogSectionId === sectionId)

// Mueve un producto dentro de su sección intercambiándolo con el vecino de la misma sección.
export const moveProductInSection = (products, productId, direction) => {
  const current = products.find(product => product.productId === productId)
  if (!current?.catalogSectionId) return products

  const sectionIndexes = products.reduce((indexes, product, index) => {
    if (product.catalogSectionId === current.catalogSectionId)
      indexes.push(index)
    return indexes
  }, [])

  const position = sectionIndexes.findIndex(
    index => products[index].productId === productId
  )
  const targetPosition = position + direction
  if (targetPosition < 0 || targetPosition >= sectionIndexes.length)
    return products

  const nextProducts = [...products]
  const from = sectionIndexes[position]
  const to = sectionIndexes[targetPosition]
  ;[nextProducts[from], nextProducts[to]] = [
    nextProducts[to],
    nextProducts[from],
  ]
  return nextProducts
}

export const assignProductsToSection = (products, productIds, sectionId) => {
  const ids = new Set(productIds)
  const assigned = products.filter(product => ids.has(product.productId))
  const rest = products.filter(product => !ids.has(product.productId))

  return [
    ...rest,
    ...assigned.map(product => ({ ...product, catalogSectionId: sectionId })),
  ]
}

export const unassignSectionProducts = (products, sectionId) =>
  products.map(product =>
    product.catalogSectionId === sectionId
      ? { ...product, catalogSectionId: null }
      : product
  )

// Precio escrito por el usuario. Acepta punto decimal (1250.5), coma decimal con 1 o 2 dígitos
// (12,5) y comas de miles cuando no hay duda (1,250.50 o 1,250,000). "1,250" es ambiguo (¿1250 o
// 1.25?) y se rechaza en lugar de adivinar.
export const parsePriceValue = value => {
  const text = toTrimmedString(value).replace(/\s/g, '')
  if (!text) return null

  if (/^\d+(\.\d+)?$/.test(text)) return Number(text)
  if (/^\d+,\d{1,2}$/.test(text)) return Number(text.replace(',', '.'))
  if (
    /^\d{1,3}(,\d{3})+\.\d+$/.test(text) ||
    /^\d{1,3}(,\d{3}){2,}$/.test(text)
  )
    return Number(text.replace(/,/g, ''))

  return NaN
}

export const isValidSpecialPrice = value => {
  if (toTrimmedString(value) === '') return true
  const price = parsePriceValue(value)
  return Number.isFinite(price) && price > 0
}

// Devuelve la clave de traducción del primer error de secciones o null.
export const validateCatalogSections = ({ sections, products }) => {
  if (!sections?.length) return 'SECTIONS_REQUIRED'
  if (sections.some(section => !toTrimmedString(section.name)))
    return 'SECTION_NAME_REQUIRED'
  if (!products?.some(product => product.catalogSectionId))
    return 'SECTION_PRODUCTS_REQUIRED'
  return null
}

export const buildCatalogPayload = ({
  values,
  productCustomListId,
  companyId,
}) => {
  const sectionIds = new Set(
    values.sections.map(section => section.catalogSectionId)
  )
  const orderBySection = {}

  return {
    ProductCustomListId: productCustomListId,
    CompanyId: companyId,
    Name: toTrimmedString(values.name),
    CoverImageUrl: values.coverImageUrl || null,
    PriceListId: values.priceListId,
    ContactPhone: formatContactPhone(values.contactPhone),
    ContactEmail: toTrimmedString(values.contactEmail),
    EndDate: values.endDate,
    Sections: values.sections.map((section, index) => ({
      CatalogSectionId: section.catalogSectionId,
      Name: toTrimmedString(section.name),
      DisplayOrder: index + 1,
      BannerImageUrl: section.bannerImageUrl || null,
      IconUrl: section.iconUrl || null,
      SecondaryImages: section.secondaryImages.map((fileUrl, imageIndex) => ({
        FileUrl: fileUrl,
        DisplayOrder: imageIndex + 1,
      })),
    })),
    Products: values.products.map(product => {
      const sectionId = sectionIds.has(product.catalogSectionId)
        ? product.catalogSectionId
        : null
      const key = sectionId || 'unassigned'
      orderBySection[key] = (orderBySection[key] || 0) + 1
      const specialPrice = parsePriceValue(product.specialPrice)

      return {
        ProductId: product.productId,
        Sku: product.sku,
        CatalogSectionId: sectionId,
        DisplayOrder: orderBySection[key],
        SpecialPrice: Number.isFinite(specialPrice) ? specialPrice : null,
        CustomImageUrl: product.customImageUrl || null,
      }
    }),
  }
}

const quoteCsvValue = value => {
  const text = toTrimmedString(value)
  return /[",;\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

// Texto libre (SKU): si empieza como una fórmula de Excel (=, +, -, @, tab, CR) se antepone ' para
// que se lea como texto. MDL09 quita ese ' al procesar el archivo.
const escapeCsvText = value => {
  const text = toTrimmedString(value)
  return quoteCsvValue(/^[=+\-@\t\r]/.test(text) ? `'${text}` : text)
}

// Plantilla del batchtemplate de MDL26: las columnas van por posición (ID del catálogo, SKU, Precio,
// URL de imagen). Se genera con el catálogo y sus SKU para que solo se completen Precio e imagen.
// El precio y la URL solo se escriben si son válidos, así nunca llevan el ' del escape de fórmulas.
export const buildCatalogCsvTemplate = (products, catalogId) => {
  const rows = products.map(product => {
    const price = parsePriceValue(product.specialPrice)
    const imageUrl = toTrimmedString(product.customImageUrl)
    return [
      quoteCsvValue(catalogId),
      escapeCsvText(product.sku),
      Number.isFinite(price) && price > 0 ? String(price) : '',
      /^https:\/\//i.test(imageUrl) ? quoteCsvValue(imageUrl) : '',
    ].join(',')
  })
  return [CATALOG_CSV_HEADERS.join(','), ...rows].join('\r\n')
}

// Resume la respuesta de mdl26/batch/fromBlobFile. MDL26 devuelve un arreglo con un elemento por
// segmento ({ status, message, readRowsQty, processedRowsQty }) o, si falla antes de procesar,
// un arreglo de errores ({ statusCode, message }).
export const extractCatalogBatchResult = segments =>
  (Array.isArray(segments) ? segments : []).reduce(
    (result, segment) => {
      const failed =
        segment?.statusCode !== undefined ||
        (segment?.status !== undefined && segment.status !== 200)

      if (failed) {
        return {
          ...result,
          errors: [...result.errors, segment?.message || ''].filter(Boolean),
        }
      }

      return {
        ...result,
        readRowsQty: result.readRowsQty + (Number(segment?.readRowsQty) || 0),
        processedRowsQty:
          result.processedRowsQty + (Number(segment?.processedRowsQty) || 0),
      }
    },
    { readRowsQty: 0, processedRowsQty: 0, errors: [] }
  )

// Mensaje del toast al terminar la carga masiva: clave de traducción, parámetros y severidad.
export const getCatalogBatchToast = result => {
  if (result?.fileError)
    return {
      messageKey: `QUICK_LIST_CATALOG.VALIDATION.${result.fileError}`,
      severity: 'error',
    }
  if (!result || result.failed)
    return {
      messageKey: 'QUICK_LIST_CATALOG.PRODUCTS.CSV_FAIL',
      severity: 'error',
    }

  const params = {
    processed: result.processedRowsQty,
    read: result.readRowsQty,
  }
  if (result.processedRowsQty === 0)
    return {
      messageKey: 'QUICK_LIST_CATALOG.PRODUCTS.CSV_TOAST_NO_CHANGES',
      params,
      severity: 'warning',
    }
  if (result.processedRowsQty < result.readRowsQty)
    return {
      messageKey: 'QUICK_LIST_CATALOG.PRODUCTS.CSV_TOAST_PARTIAL',
      params,
      severity: 'warning',
    }
  return {
    messageKey: 'QUICK_LIST_CATALOG.PRODUCTS.CSV_TOAST_SUCCESS',
    params,
    severity: 'success',
  }
}

// Aplica al formulario el precio especial y la imagen que la carga masiva cambió en la base de datos.
// Solo se toman los productos cuyo valor guardado cambió, para no pisar cambios sin guardar.
export const mergeBulkUpdatedProducts = (
  products,
  previousCatalog,
  updatedCatalog
) => {
  const savedValues = catalog =>
    new Map(
      (catalog?.Products || []).map(product => [
        normalizeId(product.ProductId),
        {
          specialPrice:
            product.SpecialPrice !== null && product.SpecialPrice !== undefined
              ? String(product.SpecialPrice)
              : '',
          customImageUrl: product.CustomImageUrl || '',
        },
      ])
    )
  const before = savedValues(previousCatalog)
  const after = savedValues(updatedCatalog)

  return products.map(product => {
    const previous = before.get(product.productId)
    const next = after.get(product.productId)
    if (!next) return product

    return {
      ...product,
      ...(next.specialPrice !== previous?.specialPrice
        ? { specialPrice: next.specialPrice }
        : {}),
      ...(next.customImageUrl !== previous?.customImageUrl
        ? { customImageUrl: next.customImageUrl }
        : {}),
    }
  })
}
